import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoginInfo, useOptions } from '../../hooks';
import type { Fan, IdpEventEmitter } from '../../types/idp-types';
import { UserEvents } from '../../types/idp-types';
import { getCurrentUser, idpInit } from '../../utils/IdpUtils';

const LoginInfo = () => {
  const {
    i18n: { language: i18nLanguage },
  } = useTranslation();
  const { setIdpUser, setIsLogged } = useLoginInfo();
  const { cup, language: optionsLanguage } = useOptions();

  // Prefer widget/options language (e.g. "ar"), fallback to i18n language
  const locale = (optionsLanguage || i18nLanguage || 'en').toLowerCase();

  const updateUser = useCallback(
    (sub: string, isLogged: boolean) => {
      setIdpUser(sub);
      setIsLogged(isLogged);
    },
    [setIdpUser, setIsLogged],
  );

  useEffect(() => {
    let eventEmitter: IdpEventEmitter;

    const handleUserChange = ({ user }: { user: Fan }) => {
      updateUser(user?.sub, true);
    };

    const initIdp = async () => {
      try {
        const idp = await idpInit(locale, cup);

        eventEmitter = window.IdpManager?.getEventEmitter?.();
        if (!eventEmitter) return;

        eventEmitter.on(UserEvents.LOGIN, handleUserChange);
        eventEmitter.on(UserEvents.LOGIN_SILENT, handleUserChange);
        eventEmitter.on(UserEvents.FAN_LOADED, handleUserChange);
        eventEmitter.on(UserEvents.FAN_UPDATED, handleUserChange);

        const logged = await idp.isFanLoggedIn();
        const user = await getCurrentUser();

        updateUser(user?.value?.sub, logged);
      } catch (e) {
        console.error(e);
      }
    };

    initIdp();

    return () => {
      if (eventEmitter) {
        eventEmitter.off(UserEvents.LOGIN, handleUserChange);
        eventEmitter.off(UserEvents.LOGIN_SILENT, handleUserChange);
        eventEmitter.off(UserEvents.FAN_LOADED, handleUserChange);
        eventEmitter.off(UserEvents.FAN_UPDATED, handleUserChange);
      }
    };
  }, [locale, updateUser, cup]);

  return null;
};

export default memo(LoginInfo);
